import * as Tooltip from '@radix-ui/react-tooltip';
import React from 'react';
import type { VoiceTenantCallAggregate } from '@reach/shared-types/voice-agent-calls';
import { formatCost, formatDuration, pct } from './voice-utils';
import styles from './VoiceTenantRow.module.css';

interface VoiceTenantRowProps {
  rank: number;
  tenant: VoiceTenantCallAggregate;
}

export const VoiceTenantRow: React.FC<VoiceTenantRowProps> = ({ rank, tenant }) => {
  const classifiedSentimentTotal =
    tenant.sentimentPositive + tenant.sentimentNegative + tenant.sentimentNeutral;
  const hasSentimentData = classifiedSentimentTotal > 0;

  const positivePct = pct(tenant.sentimentPositive, classifiedSentimentTotal);
  const neutralPct = pct(tenant.sentimentNeutral, classifiedSentimentTotal);
  const negativePct = pct(tenant.sentimentNegative, classifiedSentimentTotal);

  const transferPct =
    tenant.totalCalls > 0 ? ((tenant.transferredCount / tenant.totalCalls) * 100).toFixed(1) : '0';

  return (
    <tr className={styles.row}>
      <td className={styles.rank}>{rank}</td>
      <td className={styles.tenantCell}>
        <div className={styles.tenantName}>{tenant.tenantName}</div>
      </td>
      <td className={styles.totalCalls}>{tenant.totalCalls.toLocaleString()}</td>
      <td className={styles.transferred}>
        {tenant.transferredCount}
        <span className={styles.transferredPct}>({transferPct}%)</span>
      </td>
      <td className={styles.durationCell}>
        <div>{formatDuration(tenant.totalDurationSecs)}</div>
        <div className={styles.durationAvg}>Avg: {formatDuration(tenant.avgDurationSecs)}</div>
      </td>
      <td className={styles.cost}>{formatCost(tenant.totalCostCents)}</td>
      <td>
        {hasSentimentData ? (
          <div className={styles.sentimentBar}>
            <div className={styles.barContainer}>
              <div className={styles.barPositive} style={{ width: `${positivePct}%` }} />
              <div className={styles.barNeutral} style={{ width: `${neutralPct}%` }} />
              <div className={styles.barNegative} style={{ width: `${negativePct}%` }} />
            </div>
            <Tooltip.Provider delayDuration={200}>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span className={`${styles.sentimentLabel} cursor-help`}>
                    {Math.round(positivePct + neutralPct)}% Satisfied
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="top"
                    sideOffset={4}
                    className="z-50 max-w-xs px-3 py-2 text-xs text-white bg-gray-900 rounded-md shadow-md">
                    "Satisfied" = positive + neutral calls as a percentage of all calls with detected
                    sentiment.
                    <Tooltip.Arrow className="fill-gray-900" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        ) : (
          <span className={styles.sentimentNone}>-</span>
        )}
      </td>
    </tr>
  );
};
